/* eslint-disable react/no-array-index-key */
import React, { FC, useRef, useMemo } from 'react';
import { Button, Table } from 'antd';
import { ColumnsType } from 'antd/lib/table';
import moment from 'moment';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';

import { Payroll } from '~/types';
import { AmountField, Space, StatusField } from '~/components';
import { statusCell } from '~/utils';
import Cell from '../components/Cell';
import { usePath, usePermissions } from '~/hooks';
import layout from '~/hooks/layout';
import PayrollCard from './PayrollCard';
import { useInfiniteQuery } from 'react-query';
import { getPayrolls } from '~/api/payrolls';
import { payrollResourceKey } from '~/pages/payrolls';

interface Props {
  className?: string;
  handleOpen: (data: Payroll) => void;
}

const Payrolls: FC<Props> = (props) => {
  const { className, handleOpen } = props;
  const {
    params: { filter },
  } = usePath();

  const { data, fetchNextPage, hasNextPage, isLoading } = useInfiniteQuery(
    [payrollResourceKey, JSON.stringify(filter)],
    ({ pageParam }) => getPayrolls({ ...filter, next: pageParam, limit: 30 })(),
    {
      getNextPageParam: (lastPage) => lastPage.data?.cursors?.next,
      cacheTime: 0, // it helps not to reload all previous loaded pages when filters are reset
    }
  );
  const payrolls = data?.pages.map(({ data }) => data.payrolls ?? []).flat() ?? [];

  const { t } = useTranslation('payrolls');

  const columns = useMemo<ColumnsType<Payroll>>(
    () => [
      statusCell(),
      {
        title: t('Posted'),
        dataIndex: 'postedAmount',
        align: 'right',
        render: (_, record) => {
          return <Cell.ColoredAmount amount={record.postedAmount} />;
        },
      },
      {
        title: t('Instructed'),
        dataIndex: 'instructedAmount',
        align: 'right',
        render: (_, record) => {
          return <AmountField amount={record.instructedAmount} />;
        },
      },
      {
        title: t('Number of payments'),
        dataIndex: 'records',
        align: 'right',
        width: 150,
        render: (_, record) => {
          return record?.recordsTotal;
        },
      },
      {
        title: t('Description'),
        dataIndex: 'description',
        render: (_, record) => {
          return <Cell.Description record={record} />;
        },
      },
      {
        title: t('Date and time'),
        dataIndex: 'createdAt',
        render: (_, record) => {
          return <div>{moment(record.createdAt).format('LL LT')}</div>;
        },
      },
      {
        title: t('Executed by'),
        dataIndex: 'actor',
        render: (_, record) => {
          return <Cell.Employee record={record} />;
        },
      },
      {
        title: t('Status'),
        dataIndex: 'status',
        render: (_, record) => {
          return <StatusField data={record.status} addLabel={false} />;
        },
      },
    ],
    [t]
  );

  const buttonRef = useRef<HTMLButtonElement>(null);
  const hasAccessView = usePermissions('business.payrolls.view');

  const isMobile = layout.useIsMobile();

  return (
    <div className={className}>
      {isMobile ? (
        <Space direction="vertical" size={16}>
          {payrolls?.map((item) => {
            return (
              <PayrollCard
                key={item.id}
                record={item}
                onClick={() => hasAccessView && handleOpen(item)}
              />
            );
          })}
        </Space>
      ) : (
        <Table
          columns={columns}
          dataSource={payrolls}
          loading={isLoading}
          rowKey={(record) => record.id}
          pagination={false}
          onRow={(record) => ({
            onClick: () => hasAccessView && handleOpen(record),
          })}
        />
      )}
      <div className="Payrolls-LoadMore">
        <Button
          ref={buttonRef}
          loading={isLoading}
          disabled={!hasNextPage}
          onClick={() => {
            buttonRef.current?.blur();
            void fetchNextPage();
          }}
        >
          {hasNextPage ? t('Load more') : t('All loaded')}
        </Button>
      </div>
    </div>
  );
};

export default styled(Payrolls)`
  .ant-table-row {
    background-color: #fff;
    cursor: pointer;
  }

  .Payrolls-LoadMore {
    padding: 16px;
    display: flex;
    justify-content: center;
  }
`;
